import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { 
  ArrowLeft, Volume2, ChevronDown, ChevronUp, Check, BookOpen,
  Lightbulb, MessageCircle, Target, Save, Loader2, Headphones, ExternalLink
} from "lucide-react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";

const SoundHealingDetailPage = () => {
  const navigate = useNavigate();
  const { moduleId } = useParams();
  const { token } = useAuth();
  const [module, setModule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [reflections, setReflections] = useState({});
  const [savingReflection, setSavingReflection] = useState(null);
  
  // Expandable sections
  const [expandedIntro, setExpandedIntro] = useState(true);
  const [expandedConcepts, setExpandedConcepts] = useState(false);
  const [expandedPrompts, setExpandedPrompts] = useState(false);
  const [expandedPractice, setExpandedPractice] = useState(false);
  const [expandedListening, setExpandedListening] = useState(false);

  useEffect(() => {
    fetchModule();
    fetchReflections();
  }, [moduleId]);

  const fetchReflections = async () => {
    try {
      const response = await axios.get(`${API}/reflections/sound_healing/${moduleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.reflections) {
        const reflectionMap = {};
        response.data.reflections.forEach(r => {
          reflectionMap[r.prompt_index] = r.content;
        });
        setReflections(reflectionMap);
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error("Failed to load reflections:", error);
      }
    }
  };

  const saveReflection = async (promptIndex, prompt) => {
    const content = reflections[promptIndex];
    if (!content?.trim()) return;
    
    setSavingReflection(promptIndex);
    try {
      await axios.post(`${API}/reflections/save`, {
        module_type: 'sound_healing',
        module_id: parseInt(moduleId),
        prompt_index: promptIndex,
        prompt_text: prompt,
        content: content
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Reflection saved!");
    } catch (error) {
      toast.error("Failed to save reflection");
    } finally {
      setSavingReflection(null);
    }
  };

  const goToJournalWithPrompt = (prompt) => {
    navigate(`/journal?prompt=${encodeURIComponent(prompt)}`);
  };

  const fetchModule = async () => {
    try {
      const response = await axios.get(`${API}/sound-healing/modules/${moduleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setModule(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Premium subscription required for this session");
        navigate("/sound-healing");
      } else if (error.response?.status === 404) {
        toast.error("Session not found");
        navigate("/sound-healing");
      } else {
        console.error("Error fetching module:", error);
        toast.error("Unable to load session");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async () => {
    setCompleting(true);
    try {
      const response = await axios.post(
        `${API}/sound-healing/modules/${moduleId}/complete`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setModule(prev => ({ ...prev, is_completed: true }));
      
      if (response.data.badge_awarded) {
        toast.success("🎵 Congratulations! You've earned the Sound Healing Mastery badge!");
      } else {
        toast.success("Session completed! Keep exploring the healing power of sound.");
      }
    } catch (error) {
      toast.error("Unable to mark session as complete");
    } finally {
      setCompleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen hero-gradient flex items-center justify-center">
        <div className="animate-breathe">
          <div className="w-16 h-16 rounded-full bg-cyan-500/20 flex items-center justify-center">
            <Volume2 className="w-8 h-8 text-cyan-600" strokeWidth={1.5} />
          </div>
        </div>
      </div>
    );
  }

  if (!module) return null;

  return (
    <div className="min-h-screen hero-gradient" data-testid="sound-healing-detail-page">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate("/sound-healing")}
            className="text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            All Sessions
          </Button>
          <div className="flex items-center gap-2">
            <Volume2 className="w-5 h-5 text-cyan-600" strokeWidth={1.5} />
            <span className="text-sm text-muted-foreground">Session {module.id}</span>
          </div>
          <div className="w-24"></div>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-10">
          <span className="inline-block text-sm text-cyan-600 bg-cyan-100 dark:bg-cyan-900/30 px-3 py-1 rounded-full mb-4">
            Session {module.id} • {module.estimated_time}
          </span>
          <h1 className="font-heading text-3xl md:text-4xl font-semibold text-foreground mb-3">
            {module.title}
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {module.subtitle}
          </p>
        </div>

        {/* Completion Status */}
        {module.is_completed && (
          <Card className="glass-card rounded-2xl border-0 mb-8 bg-cyan-50/50 dark:bg-cyan-950/20">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center">
                <Check className="w-5 h-5 text-cyan-600" />
              </div>
              <div>
                <p className="font-medium text-foreground">Session Completed</p>
                <p className="text-sm text-muted-foreground">
                  Completed on {new Date(module.completed_at).toLocaleDateString()}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Introduction Section */}
        <Card className="glass-card rounded-2xl border-0 mb-6">
          <CardContent className="p-0">
            <button
              className="w-full p-6 flex items-center justify-between text-left"
              onClick={() => setExpandedIntro(!expandedIntro)}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-cyan-100 dark:bg-cyan-900/30 flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-cyan-600" />
                </div>
                <h2 className="font-heading text-xl font-semibold text-foreground">Introduction</h2>
              </div>
              {expandedIntro ? (
                <ChevronUp className="w-5 h-5 text-muted-foreground" />
              ) : (
                <ChevronDown className="w-5 h-5 text-muted-foreground" />
              )}
            </button>
            {expandedIntro && (
              <div className="px-6 pb-6">
                <div className="prose prose-cyan dark:prose-invert max-w-none">
                  {module.introduction?.split('\n\n').map((paragraph, i) => (
                    <p key={i} className="text-foreground/90 leading-relaxed mb-4 last:mb-0">
                      {paragraph.trim()}
                    </p>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Key Concepts Section */}
        <Card className="glass-card rounded-2xl border-0 mb-6">
          <CardContent className="p-0">
            <button
              className="w-full p-6 flex items-center justify-between text-left"
              onClick={() => setExpandedConcepts(!expandedConcepts)}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                  <Lightbulb className="w-5 h-5 text-amber-600" />
                </div>
                <h2 className="font-heading text-xl font-semibold text-foreground">Key Concepts</h2>
              </div>
              {expandedConcepts ? (
                <ChevronUp className="w-5 h-5 text-muted-foreground" />
              ) : (
                <ChevronDown className="w-5 h-5 text-muted-foreground" />
              )}
            </button>
            {expandedConcepts && (
              <div className="px-6 pb-6 space-y-6">
                {module.key_concepts?.map((concept, i) => (
                  <div key={i} className="bg-muted/30 rounded-xl p-5">
                    <h3 className="font-heading text-lg font-semibold text-foreground mb-2">
                      {concept.title}
                    </h3>
                    <p className="text-foreground/80 leading-relaxed">
                      {concept.description}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Reflection Prompts Section */}
        <Card className="glass-card rounded-2xl border-0 mb-6">
          <CardContent className="p-0">
            <button
              className="w-full p-6 flex items-center justify-between text-left"
              onClick={() => setExpandedPrompts(!expandedPrompts)}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-rose-600" />
                </div>
                <h2 className="font-heading text-xl font-semibold text-foreground">Reflection Prompts</h2>
              </div>
              {expandedPrompts ? (
                <ChevronUp className="w-5 h-5 text-muted-foreground" />
              ) : (
                <ChevronDown className="w-5 h-5 text-muted-foreground" />
              )}
            </button>
            {expandedPrompts && (
              <div className="px-6 pb-6">
                <p className="text-muted-foreground mb-4">
                  Take time to journal or reflect on these questions:
                </p>
                <div className="space-y-6">
                  {module.reflection_prompts?.map((prompt, i) => (
                    <div key={i} className="bg-muted/30 rounded-xl p-5">
                      <div className="flex items-start gap-3 mb-4">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center text-xs font-medium text-rose-600">
                          {i + 1}
                        </span>
                        <p className="text-foreground/90 leading-relaxed italic">{prompt}</p>
                      </div>
                      
                      {/* Reflection Input */}
                      <div className="ml-9">
                        <Textarea
                          placeholder="Write your reflection here..."
                          value={reflections[i] || ""}
                          onChange={(e) => setReflections(prev => ({ ...prev, [i]: e.target.value }))}
                          className="min-h-[100px] rounded-xl mb-3 bg-white/50 dark:bg-white/5"
                        />
                        <div className="flex gap-3">
                          <Button
                            size="sm"
                            onClick={() => saveReflection(i, prompt)}
                            disabled={savingReflection === i || !reflections[i]?.trim()}
                            className="rounded-lg"
                          >
                            {savingReflection === i ? (
                              <Loader2 className="w-4 h-4 animate-spin mr-1" />
                            ) : (
                              <Save className="w-4 h-4 mr-1" />
                            )}
                            Save
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => goToJournalWithPrompt(prompt)}
                            className="rounded-lg"
                          >
                            <BookOpen className="w-4 h-4 mr-1" />
                            Journal
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Practice Section */}
        <Card className="glass-card rounded-2xl border-0 mb-6">
          <CardContent className="p-0">
            <button
              className="w-full p-6 flex items-center justify-between text-left"
              onClick={() => setExpandedPractice(!expandedPractice)}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <Target className="w-5 h-5 text-green-600" />
                </div>
                <h2 className="font-heading text-xl font-semibold text-foreground">Practice</h2>
              </div>
              {expandedPractice ? (
                <ChevronUp className="w-5 h-5 text-muted-foreground" />
              ) : (
                <ChevronDown className="w-5 h-5 text-muted-foreground" />
              )}
            </button>
            {expandedPractice && module.practice && (
              <div className="px-6 pb-6">
                <div className="bg-green-50/50 dark:bg-green-950/20 rounded-xl p-5">
                  <h3 className="font-heading text-lg font-semibold text-foreground mb-2">
                    {module.practice.title}
                  </h3>
                  <p className="text-foreground/90 leading-relaxed mb-4">
                    {module.practice.description}
                  </p>
                  <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                    <span className="bg-green-100 dark:bg-green-900/30 px-3 py-1 rounded-full">
                      ⏱️ {module.practice.duration}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recommended Listening Section - Unique to Sound Healing */}
        {module.recommended_listening && module.recommended_listening.length > 0 && (
          <Card className="glass-card rounded-2xl border-0 mb-8">
            <CardContent className="p-0">
              <button
                className="w-full p-6 flex items-center justify-between text-left"
                onClick={() => setExpandedListening(!expandedListening)}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-cyan-100 dark:bg-cyan-900/30 flex items-center justify-center">
                    <Headphones className="w-5 h-5 text-cyan-600" />
                  </div>
                  <h2 className="font-heading text-xl font-semibold text-foreground">Recommended Listening</h2>
                </div>
                {expandedListening ? (
                  <ChevronUp className="w-5 h-5 text-muted-foreground" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-muted-foreground" />
                )}
              </button>
              {expandedListening && (
                <div className="px-6 pb-6">
                  <p className="text-muted-foreground mb-4">
                    Enhance your practice with these curated sound experiences:
                  </p>
                  <div className="space-y-3">
                    {module.recommended_listening.map((item) => (
                      <a
                        key={item.url}
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-4 p-4 bg-cyan-50/50 dark:bg-cyan-950/20 rounded-xl hover:bg-cyan-100/50 dark:hover:bg-cyan-900/30 transition-colors group"
                      >
                        <div className="w-12 h-12 rounded-lg bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                          <Volume2 className="w-6 h-6 text-cyan-600" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium text-foreground group-hover:text-cyan-600 transition-colors">
                            {item.title}
                          </h4>
                          <p className="text-sm text-muted-foreground">{item.type}</p>
                        </div>
                        <ExternalLink className="w-4 h-4 text-muted-foreground group-hover:text-cyan-600 transition-colors" />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Complete Button */}
        {!module.is_completed && (
          <div className="text-center">
            <Button
              onClick={handleComplete}
              disabled={completing}
              className="bg-cyan-600 hover:bg-cyan-700 text-white px-8 py-3 rounded-full text-lg"
              data-testid="complete-session-btn"
            >
              {completing ? (
                <>
                  <Volume2 className="w-5 h-5 mr-2 animate-spin" />
                  Completing...
                </>
              ) : (
                <>
                  <Check className="w-5 h-5 mr-2" />
                  Mark Session Complete
                </>
              )}
            </Button>
            <p className="text-sm text-muted-foreground mt-3">
              Complete all sections above before marking as done
            </p>
          </div>
        )}

        {/* Navigation between sessions */}
        <div className="flex justify-between mt-10 pt-6 border-t border-border/50">
          {module.id > 1 && (
            <Button
              variant="ghost"
              onClick={() => navigate(`/sound-healing/${module.id - 1}`)}
              className="text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Previous Session
            </Button>
          )}
          <div className="flex-1" />
          {module.id < 10 && (
            <Button
              variant="ghost"
              onClick={() => navigate(`/sound-healing/${module.id + 1}`)}
              className="text-muted-foreground hover:text-foreground"
            >
              Next Session
              <ArrowLeft className="w-4 h-4 ml-2 rotate-180" />
            </Button>
          )}
        </div>
      </main>
    </div>
  );
};

export default SoundHealingDetailPage;
